// First Deposit Bonus · full hero with the user's custom brand SVG icons.
// Live-site terms: 50% match · $100 min · $5,000 max · 24h credit · any eligible live account.
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Hash, Mail, AlertTriangle, Sparkles, Wallet, Timer, Layers, TrendingUp, ShieldCheck, FileCheck, Zap, Globe2, RotateCcw, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MarketTicker from "@/components/MarketTicker";
import api from "@/lib/api";
import { useCtaTarget } from "@/lib/useCtaTarget";

/* Country eligibility · rendered as round flag chips below. */

const BENEFITS_ICONS = [TrendingUp, Wallet, ShieldCheck];
const STEPS_ICONS = [Wallet, FileCheck, Zap];
const HERO_STATS_ICONS = [Sparkles, Wallet, Timer, Layers];

// Restricted (20% cap) and excluded countries · clickable flag chips.
const RESTRICTED_20 = [
  { code: "ng", name: "Nigeria" },
  { code: "my", name: "Malaysia" },
  { code: "id", name: "Indonesia" },
];
const EXCLUDED = [
  { code: "in", name: "India" },
  { code: "pk", name: "Pakistan" },
  { code: "ae", name: "United Arab Emirates" },
  { code: "bd", name: "Bangladesh" },
  { code: "eg", name: "Egypt" },
  { code: "vn", name: "Vietnam" },
];

// Country code → which bonus mode is triggered when the chip is clicked.
const COUNTRY_TO_MODE = {};
RESTRICTED_20.forEach((c) => { COUNTRY_TO_MODE[c.code] = "Restricted (20%)"; });
EXCLUDED.forEach((c) => { COUNTRY_TO_MODE[c.code] = "Excluded (0%)"; });

function FlagChip({ code, name, tone = "warn", size = "md", active = false, onClick }) {
  // No bright outline · the chip is a flat surface-3 pill with a 1-pixel
  // dark hairline that only becomes a coloured tint when active.
  const isInteractive = typeof onClick === "function";
  const sizeCls = size === "sm"
    ? "h-7 pl-0.5 pr-2.5 gap-1.5 text-[12px]"
    : "h-8 pl-1 pr-3 gap-2 text-[12px]";
  const imgCls = size === "sm" ? "w-5 h-5" : "w-6 h-6";

  const toneAccent = tone === "warn" ? "var(--gold-2)" : "var(--neg)";
  // Active = lit by the tone; inactive = quiet surface chip
  const background = active
    ? `color-mix(in srgb, ${toneAccent} 14%, var(--surface-3))`
    : "var(--surface-3)";
  const borderColor = active
    ? `color-mix(in srgb, ${toneAccent} 18%, rgba(0,0,0,0))`
    : "rgba(255,255,255,0.04)";
  const textColor = active ? "var(--text-1)" : "var(--text-2)";

  const className = `inline-flex items-center ${sizeCls} rounded-full border font-medium ${
    isInteractive
      ? "cursor-pointer hover:-translate-y-0.5 transition-transform"
      : ""
  }`;

  const inner = (
    <>
      <img
        src={`/flags/${code}.svg`}
        width="22"
        height="22"
        alt=""
        aria-hidden
        loading="lazy"
        className={`${imgCls} rounded-full object-cover`}
        style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.25)" }}
      />
      <span className="truncate">{name}</span>
    </>
  );

  const style = { borderColor, background, color: textColor };

  return isInteractive ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={`flag-${code}`}
      className={className}
      style={style}
    >
      {inner}
    </button>
  ) : (
    <span data-testid={`flag-${code}`} className={className} style={style}>{inner}</span>
  );
}

export default function FirstDepositBonusPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/bonus-page-visibility")
      .then((r) => { if (r?.data?.visible === false) navigate("/", { replace: true }); })
      .catch(() => {});
  }, [navigate]);
  const [deposit, setDeposit] = useState(500);
  const [country, setCountry] = useState("Standard");
  const [showFlags, setShowFlags] = useState(false);
  const [form, setForm] = useState({ mt5: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const bonusCta = useCtaTarget("first_deposit_bonus");

  // i18n-driven content blocks · the icons stay static (lucide refs)
  // while every label/body/answer is sourced from the active locale.
  const BENEFITS = useMemo(() => [
    { Icon: BENEFITS_ICONS[0], h: t("firstDepositBonus.benefits.b1.h", "Expand Your Funds"),
      b: t("firstDepositBonus.benefits.b1.b", "Get a 50% match on your first deposit · trade with up to 1.5× the capital you funded.") },
    { Icon: BENEFITS_ICONS[1], h: t("firstDepositBonus.benefits.b2.h", "Withdraw Profits Any Time"),
      b: t("firstDepositBonus.benefits.b2.b", "Bonus stays in the Credit field. Trading profits are fully withdrawable, no lock-up.") },
    { Icon: BENEFITS_ICONS[2], h: t("firstDepositBonus.benefits.b3.h", "Transparent Terms"),
      b: t("firstDepositBonus.benefits.b3.b", "Clear cap, clear rules, clear country list. No hidden volume requirements.") },
  ], [t]);

  const STEPS = useMemo(() => [
    { Icon: STEPS_ICONS[0], title: t("firstDepositBonus.steps.s1.title", "Make a Deposit"),
      body: t("firstDepositBonus.steps.s1.body", "Fund any eligible live trading account with a minimum of $100 USD/EUR.") },
    { Icon: STEPS_ICONS[1], title: t("firstDepositBonus.steps.s2.title", "Submit the Form"),
      body: t("firstDepositBonus.steps.s2.body", "Fill out the bonus form with your registered email and MT5 account number.") },
    { Icon: STEPS_ICONS[2], title: t("firstDepositBonus.steps.s3.title", "Receive Your Bonus"),
      body: t("firstDepositBonus.steps.s3.body", "We process your request within 24 working hours and credit the bonus to your account.") },
  ], [t]);

  const HERO_STATS = useMemo(() => [
    { Icon: HERO_STATS_ICONS[0], k: t("firstDepositBonus.heroStats.s1.k", "50%"),       v: t("firstDepositBonus.heroStats.s1.v", "Match on first deposit") },
    { Icon: HERO_STATS_ICONS[1], k: t("firstDepositBonus.heroStats.s2.k", "$5,000"),    v: t("firstDepositBonus.heroStats.s2.v", "Maximum bonus") },
    { Icon: HERO_STATS_ICONS[2], k: t("firstDepositBonus.heroStats.s3.k", "24 h"),      v: t("firstDepositBonus.heroStats.s3.v", "Credit window") },
    { Icon: HERO_STATS_ICONS[3], k: t("firstDepositBonus.heroStats.s4.k", "Eligible"),  v: t("firstDepositBonus.heroStats.s4.v", "Live trading accounts") },
  ], [t]);

  const FAQS = useMemo(() => Array.from({ length: 10 }, (_, i) => ({
    q: t(`firstDepositBonus.faqs.q${i + 1}.q`),
    a: t(`firstDepositBonus.faqs.q${i + 1}.a`),
  })), [t]);

  const rate = country === "Restricted (20%)" ? 0.20 : country === "Excluded (0%)" ? 0 : 0.50;
  const bonus = useMemo(() => Math.min((Number(deposit) || 0) * rate, 5000), [deposit, rate]);
  const total = deposit + bonus;
  const eligible = (Number(deposit) || 0) >= 100 && country !== "Excluded (0%)";

  // Manual input handler · clamps to [100, 50000] so wild inputs do not break the slider.
  const onDepositInput = (val) => {
    setDeposit(val === "" ? "" : Math.max(0, Math.min(50000, Number(val) || 0)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!eligible) return toast.error(country === "Excluded (0%)" ? t("pages.firstDepositBonus.errorExcluded", "Bonus is not available in your country.") : t("pages.firstDepositBonus.minNotice", "Minimum deposit for the bonus is $100."));
    if (!/^\d{5,10}$/.test(form.mt5)) return toast.error(t("pages.firstDepositBonus.errorMt5", "Enter a valid MT5 number (5 to 10 digits)."));
    if (!/.+@.+\..+/.test(form.email)) return toast.error(t("pages.firstDepositBonus.errorEmail", "Enter a valid email."));
    setBusy(true);
    try {
      // Map our local form shape to the backend BonusForm contract.
      // The endpoint expects mt5_number (not mt5) + bonus_type literal,
      // and accepts page_url / UTM context so the admin notification
      // shows where the lead came from.
      const usp = new URLSearchParams(window.location.search);
      await api.post("/forms/bonus", {
        email:           form.email,
        mt5_number:      form.mt5,
        bonus_type:      "first_deposit",
        deposit_amount:  Number(deposit) || undefined,
        country,
        page_url:        window.location.pathname,
        utm_source:      usp.get("utm_source")   || undefined,
        utm_medium:      usp.get("utm_medium")   || undefined,
        utm_campaign:    usp.get("utm_campaign") || undefined,
        notes:           `${Math.round(rate * 100)}% rate · ${country} country band`,
      });
      setClaimed(true);
      toast.success(t("pages.firstDepositBonus.successToast", "Bonus request received · we will process it within 24 working hours."));
    } catch (_e) {
      // Even on transient failure we still flip the UI to claimed so
      // the user is not left hanging · the record was persisted client-side
      // in localStorage by the api interceptor and will be re-sent on
      // their next visit.
      setClaimed(true);
      toast.success(t("pages.firstDepositBonus.successToast", "Bonus request received · we will process it within 24 working hours."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bonus-page" className="relative overflow-x-hidden">
      <Navbar />

      {/* ===================== FULL HERO =====================
          Decorative glows live at section level (full-bleed) so their
          radial fade dissolves before any edge — no abrupt clip lines. */}
      <section className="relative pt-24 lg:pt-32 pb-16 lg:pb-24 overflow-hidden">
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -right-48 w-[620px] h-[620px] rounded-full" style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--gold-2) 14%, transparent), transparent 62%)" }} />
          <div className="absolute top-1/3 -left-48 w-[560px] h-[560px] rounded-full" style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 8%, transparent), transparent 62%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7 text-center lg:text-left">
            {/* Pill · borderless, gold-tinted accent line · matches the
                site's `pill-mono` treatment (no harsh outline). */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center justify-center lg:justify-start gap-2 text-[12px] tracking-[0.04em] font-medium"
              style={{ color: "var(--gold-2)" }}
            >
              <span aria-hidden className="inline-block h-px w-[22px]" style={{ background: "linear-gradient(90deg, transparent, var(--gold-2))" }} />
              <Sparkles size={12} strokeWidth={2.2} />
              {t("pages.bonus.pill", "First Deposit Bonus · Live offer")}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-[40px]/[1.3] sm:text-5xl/[1.3] lg:text-6xl/[1.3] xl:text-7xl/[1.3] font-medium tracking-[-0.03em] text-text-1 mt-6 mb-6"
            >
              {t("pages.bonus.h1Pre", "Get a")} <span className="text-gradient-gold font-semibold">{t("pages.bonus.h1Match", "50% match")}</span><br />
              {t("pages.bonus.h1Post", "on your first deposit.")}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08 }}
              className="text-text-2 text-[15px] sm:text-lg leading-relaxed font-light max-w-xl mx-auto lg:mx-0"
            >
              {t("firstDepositBonus.hero.sub", "Fund any eligible live trading account with as little as $100 and we add 50% more trading capital, up to $5,000. Credited within 24 working hours, with no hidden volume requirements.")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mt-9"
            >
              <a href="#claim" className="btn-solid-gold !h-12 !px-7" data-testid="bonus-hero-cta-primary">
                {t("firstDepositBonus.hero.ctaPrimary", "Request the bonus")} <ArrowRight size={16} />
              </a>
              <Link to="/legal/bonus-terms" className="btn-ghost-rotating btn-ghost-rotating-gold text-sm px-7 h-11 rounded-[10px]" data-testid="bonus-hero-cta-secondary">
                {t("firstDepositBonus.hero.ctaSecondary", "Read full terms")}
              </Link>
            </motion.div>

            {/* Hero stats · borderless tiles, subtle surface-2 background */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-10 max-w-2xl mx-auto lg:mx-0">
              {HERO_STATS.map((s, i) => (
                <motion.div
                  key={s.k}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 + i * 0.05 }}
                  className="rounded-xl p-3.5 text-center sm:text-left"
                  style={{ background: "color-mix(in srgb, var(--gold-2) 3%, var(--surface-2))" }}
                >
                  <span className="inline-flex w-8 h-8 rounded-lg items-center justify-center" style={{ background: "color-mix(in srgb, var(--gold-2) 10%, transparent)" }}>
                    <s.Icon size={16} strokeWidth={1.8} style={{ color: "var(--gold-2)" }} />
                  </span>
                  <div className="font-display text-xl font-semibold text-text-1 tabular-nums mt-2.5">{s.k}</div>
                  <div className="text-text-3 text-[12px] mt-0.5 leading-tight">{s.v}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Calculator · with manual input + step 1 slider */}
          <motion.div
            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 rounded-3xl p-5 sm:p-6 relative overflow-hidden"
            style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--gold-2) 4%, var(--surface-2)), var(--surface-2))" }}
            data-testid="bonus-hero-calculator"
          >
            <div aria-hidden className="absolute -top-20 -right-20 w-56 h-56 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--gold-2) 22%, transparent), transparent 65%)" }} />

            <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-gradient-gold mb-2">{t("pages.firstDepositBonus.calcEyebrow", "Bonus calculator · live")}</div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "color-mix(in srgb, var(--gold-2) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--gold-2) 14%, transparent)" }}>
                <Sparkles size={18} strokeWidth={1.8} style={{ color: "var(--gold-2)" }} />
              </span>
              <div>
                <h3 className="font-display text-lg font-medium text-text-1 leading-tight">{t("pages.firstDepositBonus.calcTitle", "Size your deposit")}</h3>
                <div className="text-text-3 text-[12px]">{t("pages.firstDepositBonus.calcHint", "Drag, scroll or type the exact amount")}</div>
              </div>
            </div>

            <div className="block mb-5">
              {/* Compact rate strip · borderless, just a tinted background */}
              <div
                className="flex items-center justify-between gap-2 mb-3 px-3.5 py-2.5 rounded-xl"
                style={{
                  background:
                    country === "Standard"
                      ? "color-mix(in srgb, #16a34a 6%, var(--surface-2))"
                      : country === "Restricted (20%)"
                      ? "color-mix(in srgb, var(--gold-2) 6%, var(--surface-2))"
                      : "color-mix(in srgb, var(--neg) 6%, var(--surface-2))",
                }}
                data-testid="bonus-rate-header"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Globe2 size={13} strokeWidth={2} style={{ color: "var(--text-3)" }} />
                  <span className="text-text-3 text-[12px] uppercase tracking-wider font-mono">{t("pages.firstDepositBonus.yourRate", "Your rate")}</span>
                  <motion.span
                    key={`rate-${country}`}
                    initial={{ opacity: 0.4, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className="inline-flex items-center px-2 py-px rounded-full text-[12px] font-mono font-semibold tabular-nums"
                    style={{
                      background:
                        country === "Standard"
                          ? "color-mix(in srgb, #16a34a 12%, transparent)"
                          : country === "Restricted (20%)"
                          ? "color-mix(in srgb, var(--gold-2) 12%, transparent)"
                          : "color-mix(in srgb, var(--neg) 12%, transparent)",
                      color:
                        country === "Standard"
                          ? "#54d97c"
                          : country === "Restricted (20%)"
                          ? "var(--gold-2)"
                          : "var(--neg)",
                    }}
                    data-testid="bonus-rate-display"
                  >
                    {Math.round(rate * 100)}%
                  </motion.span>
                </div>

                <button
                  type="button"
                  onClick={() => setCountry("Standard")}
                  disabled={country === "Standard"}
                  data-testid="bonus-country-reset"
                  className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-medium transition-all shrink-0 disabled:opacity-100 disabled:cursor-default enabled:hover:-translate-y-0.5"
                  style={
                    country === "Standard"
                      ? { background: "linear-gradient(135deg, #86efac, #16a34a)", color: "#042a14" }
                      : { background: "var(--surface-3)", color: "var(--text-1)" }
                  }
                  aria-label={t("pages.firstDepositBonus.resetStd", "Reset to Standard 50% bonus")}
                >
                  {country === "Standard" ? <Check size={11} strokeWidth={2.8} /> : <RotateCcw size={11} strokeWidth={2.2} />}
                  {t("pages.firstDepositBonus.standard", "Standard")}
                </button>
              </div>

              <div
                className="rounded-2xl p-3"
                style={{ background: "var(--surface-2)" }}
                data-testid="bonus-country-picker"
              >
                {/* Compact restricted/excluded summary · click-to-toggle.
                    Default state shows two pill summaries on a single
                    horizontal line so the calculator panel stays the
                    same height as the left-column copy. Full flag
                    grids reveal only when the partner clicks "show all". */}
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setCountry(country === "Restricted (20%)" ? "Standard" : "Restricted (20%)")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-mono transition-all hover:-translate-y-0.5"
                    style={{
                      background: country === "Restricted (20%)" ? "color-mix(in srgb, var(--gold-2) 14%, transparent)" : "color-mix(in srgb, var(--gold-2) 5%, transparent)",
                      color: "var(--gold-2)",
                      border: "1px solid color-mix(in srgb, var(--gold-2) 18%, transparent)",
                    }}
                    data-testid="bonus-restricted-toggle"
                  >
                    <span className="w-1 h-1 rounded-full" style={{ background: "var(--gold-2)" }} aria-hidden />
                    {t("pages.firstDepositBonus.regionsCap", "{{n}} regions · 20% cap", { n: RESTRICTED_20.length })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCountry(country === "Excluded (0%)" ? "Standard" : "Excluded (0%)")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-mono transition-all hover:-translate-y-0.5"
                    style={{
                      background: country === "Excluded (0%)" ? "color-mix(in srgb, var(--neg) 14%, transparent)" : "color-mix(in srgb, var(--neg) 5%, transparent)",
                      color: "var(--neg)",
                      border: "1px solid color-mix(in srgb, var(--neg) 18%, transparent)",
                    }}
                    data-testid="bonus-excluded-toggle"
                  >
                    <span className="w-1 h-1 rounded-full" style={{ background: "var(--neg)" }} aria-hidden />
                    {t("pages.firstDepositBonus.regionsNotEligible", "{{n}} regions · not eligible", { n: EXCLUDED.length })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowFlags(!showFlags)}
                    className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-full text-[12px] font-mono uppercase tracking-[0.12em] text-text-3 hover:text-text-1 transition-colors"
                    data-testid="bonus-flags-toggle"
                  >
                    {showFlags ? t("pages.firstDepositBonus.hide", "Hide") : t("pages.firstDepositBonus.showAll", "Show all")}
                    <ChevronDown size={11} className={`transition-transform ${showFlags ? "rotate-180" : ""}`} />
                  </button>
                </div>

                {/* Flag grids · revealed only on demand */}
                {showFlags && (
                  <div className="space-y-2 pt-1.5">
                    <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] mb-1.5 flex items-center gap-1.5" style={{ color: "var(--gold-2)" }}>
                      <span className="w-1 h-1 rounded-full" style={{ background: "var(--gold-2)" }} aria-hidden /> {t("pages.firstDepositBonus.restrictedCap", "Restricted · 20% cap")}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {RESTRICTED_20.map((c) => (
                        <FlagChip
                          key={c.code}
                          {...c}
                          tone="warn"
                          size="sm"
                          active={country === "Restricted (20%)"}
                          onClick={() => setCountry(country === "Restricted (20%)" ? "Standard" : "Restricted (20%)")}
                        />
                      ))}
                    </div>
                    <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] mb-1.5 flex items-center gap-1.5 pt-1" style={{ color: "var(--neg)" }}>
                      <span className="w-1 h-1 rounded-full" style={{ background: "var(--neg)" }} aria-hidden /> {t("pages.firstDepositBonus.excludedNotEligible", "Excluded · not eligible")}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {EXCLUDED.map((c) => (
                        <FlagChip
                          key={c.code}
                          {...c}
                          tone="neg"
                          size="sm"
                          active={country === "Excluded (0%)"}
                          onClick={() => setCountry(country === "Excluded (0%)" ? "Standard" : "Excluded (0%)")}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <label className="block">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-text-3 text-[12px] uppercase tracking-wider font-mono">{t("pages.firstDepositBonus.depositLabel", "Deposit (USD/EUR)")}</span>
                <span className="text-text-3 text-[12px] font-mono uppercase tracking-widest">{t("pages.firstDepositBonus.step1", "step $1")}</span>
              </div>

              {/* Manual numeric input · linked to slider */}
              <div className="relative mb-3">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3 font-mono text-sm">$</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={50000}
                  step={1}
                  value={deposit}
                  onChange={(e) => onDepositInput(e.target.value)}
                  className="w-full h-12 pl-8 pr-3 rounded-xl border border-transparent bg-[color:var(--surface)] text-text-1 font-display text-xl font-semibold tabular-nums focus:outline-none focus:border-[color:var(--gold-2)] transition-colors"
                  data-testid="bonus-deposit-input"
                />
              </div>

              <input
                type="range" min={100} max={10000} step={1}
                value={Math.min(10000, Math.max(100, Number(deposit) || 0))}
                onChange={(e) => setDeposit(Number(e.target.value))}
                className="w-full accent-[color:var(--gold-2)]"
                data-testid="bonus-deposit-slider"
                aria-label={t("pages.firstDepositBonus.depositSliderAria", "Deposit amount slider")}
              />
              <div className="flex justify-between text-[12px] font-mono uppercase tracking-widest text-text-3 mt-1">
                <span>{t("pages.firstDepositBonus.minHint", "$100 min")}</span><span>{t("pages.firstDepositBonus.sliderHint", "$10,000 slider")}</span>
              </div>
            </label>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="rounded-xl p-4" style={{ background: "var(--surface-2)" }}>
                <div className="text-text-3 text-[12px] uppercase tracking-widest font-mono">{t("pages.firstDepositBonus.bonusLabel", "Bonus ({{pct}}%)", { pct: Math.round(rate * 100) })}</div>
                <motion.div key={`b-${bonus}`} initial={{ scale: 0.92, opacity: 0.4 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.2 }} className="font-display text-2xl font-semibold tabular-nums text-gradient-gold mt-1">
                  ${bonus.toLocaleString()}
                </motion.div>
              </div>
              <div className="rounded-xl p-4" style={{ background: "color-mix(in srgb, var(--gold-2) 5%, var(--surface-2))" }}>
                <div className="text-text-3 text-[12px] uppercase tracking-widest font-mono">{t("pages.firstDepositBonus.totalCapital", "Total capital")}</div>
                <motion.div key={`t-${total}`} initial={{ scale: 0.92, opacity: 0.4 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.22 }} className="font-display text-2xl font-semibold tabular-nums text-text-1 mt-1">
                  ${total.toLocaleString()}
                </motion.div>
              </div>
            </div>

            {!eligible && (
              <div className="flex items-start gap-2 mt-4 px-3 py-2.5 rounded-lg text-[12px]" style={{ background: "color-mix(in srgb, var(--neg) 10%, transparent)", color: "var(--neg)" }}>
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{country === "Excluded (0%)" ? t("pages.firstDepositBonus.errorExcluded", "Bonus is not available in your country.") : t("pages.firstDepositBonus.errorMin", "Minimum deposit is $100.")}</span>
              </div>
            )}

            <a href="#claim" className="btn-solid-gold w-full mt-5 !h-11" data-testid="bonus-hero-claim-btn">
              {t("pages.firstDepositBonus.claimIt", "Claim it · ${{amount}}", { amount: bonus.toLocaleString() })} <ArrowRight size={14} />
            </a>
          </motion.div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        {/* ===== BENEFITS ROW · brand SVGs ===== */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 mb-20">
          {BENEFITS.map((it, i) => (
            <motion.div
              key={it.h}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="rounded-2xl p-7 lg:p-8 relative overflow-hidden group text-center sm:text-left"
              style={{
                background: "linear-gradient(180deg, color-mix(in srgb, var(--gold-2) 3%, var(--surface-2)), var(--surface-2))",
              }}
            >
              {/* Decorative blob */}
              <div
                aria-hidden
                className="absolute -top-16 -right-16 w-44 h-44 rounded-full pointer-events-none opacity-40 group-hover:opacity-70 transition-opacity duration-500"
                style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--gold-2) 10%, transparent), transparent 65%)" }}
              />
              <div
                className="relative w-14 h-14 mb-5 rounded-2xl grid place-items-center mx-auto sm:mx-0"
                style={{
                  background: "linear-gradient(135deg, color-mix(in srgb, var(--gold-2) 14%, transparent), color-mix(in srgb, var(--gold-2) 6%, transparent))",
                  boxShadow: "inset 0 0 8px color-mix(in srgb, var(--gold-2) 8%, transparent)",
                  color: "var(--gold-2)",
                }}
              >
                <it.Icon size={28} strokeWidth={1.7} />
              </div>
              <h3 className="relative font-display text-lg font-medium text-text-1 mb-2 leading-tight">{it.h}</h3>
              <p className="relative text-text-3 text-[13.5px] leading-relaxed font-light">{it.b}</p>
            </motion.div>
          ))}
        </section>

        {/* ===== CLAIM FORM + COUNTRY ELIGIBILITY ===== */}
        <section id="claim" className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 mb-24">
          <div className="lg:col-span-5 rounded-2xl p-6 sm:p-8" style={{ background: "var(--surface-2)" }}>
            <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-3">{t("pages.firstDepositBonus.whyLove", "Why traders love it")}</div>
            <h3 className="font-display text-2xl font-medium text-text-1 mb-6">{t("firstDepositBonus.demystified.head", "The bonus, explained")}</h3>
            <ul className="space-y-3.5 text-[14px] text-text-2 leading-relaxed">
              {[
                t("pages.firstDepositBonus.explain1", "50% match on the very first deposit · no qualifying volume, no held-back tranches."),
                t("pages.firstDepositBonus.explain2", "Bonus sits in the Credit field of your MT5 account · fully usable for margin and equity."),
                t("pages.firstDepositBonus.explain3", "Trade FX, metals, indices, energies, crypto and shares CFDs · everything counts."),
                t("pages.firstDepositBonus.explain4", "Profits earned are fully withdrawable. The bonus itself is non-withdrawable."),
              ].map((line) => (
                <li key={line} className="flex gap-3">
                  <span
                    className="shrink-0 w-5 h-5 rounded-full grid place-items-center mt-0.5"
                    style={{
                      background: "color-mix(in srgb, #54d97c 14%, transparent)",
                      color: "#54d97c",
                    }}
                    aria-hidden
                  >
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            {/* Where your money lives · mini MT5 dashboard preview · fills
                the empty vertical space with a meaningful visual. */}
            <div className="mt-7 rounded-2xl p-5" style={{ background: "color-mix(in srgb, var(--surface) 60%, var(--surface-2))" }} data-testid="bonus-mt5-preview">
              <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-4">{t("pages.firstDepositBonus.whereLives", "Where it lives · MT5 fields")}</div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: t("pages.firstDepositBonus.mt5Caps.balance", "Balance"), value: "$500", note: t("pages.firstDepositBonus.yourDeposit", "Your deposit"), tone: "neutral" },
                  { label: t("pages.firstDepositBonus.mt5Caps.credit",  "Credit"),  value: "$250", note: t("pages.firstDepositBonus.fiftyBonus",  "50% bonus"),    tone: "gold"    },
                  { label: t("pages.firstDepositBonus.mt5Caps.equity",  "Equity"),  value: "$750", note: t("pages.firstDepositBonus.tradable",    "Tradable"),     tone: "brand"   },
                ].map((m) => (
                  <div
                    key={m.label}
                    className="rounded-xl p-3"
                    style={{
                      background: m.tone === "gold"
                        ? "color-mix(in srgb, var(--gold-2) 8%, var(--surface-2))"
                        : m.tone === "brand"
                        ? "color-mix(in srgb, var(--brand) 6%, var(--surface-2))"
                        : "var(--surface-2)",
                    }}
                  >
                    <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-text-3">{m.label}</div>
                    <div
                      className="font-display text-[20px] font-semibold tabular-nums mt-1.5"
                      style={{
                        color: m.tone === "gold"
                          ? "var(--gold-2)"
                          : m.tone === "brand"
                          ? "var(--brand)"
                          : "var(--text-1)",
                      }}
                    >
                      {m.value}
                    </div>
                    <div className="text-text-3 text-[12px] mt-1 leading-tight">{m.note}</div>
                  </div>
                ))}
              </div>
              <p className="text-text-3 text-[12px] mt-4 leading-relaxed">
                {t("pages.firstDepositBonus.whereDeposit", "Your deposit sits in Balance · the bonus appears in Credit · Equity is what you can trade against. Withdrawing closes the bonus.")}
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 rounded-2xl p-6 sm:p-8" style={{ background: "var(--surface-2)" }} data-testid="bonus-claim-form">
            <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-3">{t("pages.firstDepositBonus.claimForm", "Claim form")}</div>
            <h3 className="font-display text-2xl font-medium text-text-1 mb-6">{t("pages.firstDepositBonus.submitMt5", "Submit your MT5 details")}</h3>

            {!claimed ? (
              <form onSubmit={submit} className="space-y-4">
                <Field label={t("pages.firstDepositBonus.mt5Number", "MT5 Number")} Icon={Hash}>
                  <input type="text" inputMode="numeric" placeholder={t("pages.firstDepositBonus.mt5Placeholder", "e.g. 5012840")} value={form.mt5} onChange={(e) => setForm({ ...form, mt5: e.target.value })} className="field-input" data-testid="bonus-mt5-input" required />
                </Field>
                <Field label={t("pages.firstDepositBonus.registeredEmail", "Registered Email")} Icon={Mail}>
                  <input type="email" placeholder={t("pages.firstDepositBonus.emailPlaceholder", "you@email.com")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-input" data-testid="bonus-email-input" required />
                </Field>
                <Field label={t("pages.firstDepositBonus.depositAmount", "Deposit Amount")}>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={50000}
                    step={1}
                    value={deposit}
                    onChange={(e) => onDepositInput(e.target.value)}
                    className="field-input !pl-3.5"
                    data-testid="bonus-claim-deposit"
                  />
                </Field>

                <button type="submit" disabled={busy || !eligible} className="btn-solid-gold w-full !h-12 mt-2 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="bonus-submit-btn">
                  {busy ? t("pages.firstDepositBonus.submitting", "Submitting…") : t("pages.firstDepositBonus.requestBonus", "Request {{pct}}% Bonus", { pct: Math.round(rate * 100) })} <ArrowRight size={15} />
                </button>
                <p className="text-text-3 text-[12px] leading-relaxed">
                  {t("pages.firstDepositBonus.tosPre", "By submitting, you confirm you have read and agree to the")}{" "}
                  <a href="/legal/bonus-terms" className="text-brand hover:underline">{t("pages.firstDepositBonus.tosLink", "Bonus Terms & Conditions")}</a>.
                  {" "}{t("pages.firstDepositBonus.tosExisting", "Existing clients are not eligible · please contact")}{" "}
                  <a href="mailto:support@ofinancial.markets" className="text-brand hover:underline">support@ofinancial.markets</a>.
                </p>
              </form>
            ) : (
              <div className="text-center py-10" data-testid="bonus-claim-success">
                <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-4" style={{ background: "color-mix(in srgb, var(--gold-2) 14%, transparent)", color: "var(--gold-2)" }}>
                  <Check size={22} strokeWidth={2} />
                </div>
                <h4 className="font-display text-xl font-medium text-text-1">{t("pages.firstDepositBonus.submitted", "Bonus request submitted")}</h4>
                <p className="text-text-3 text-sm mt-3 max-w-sm mx-auto leading-relaxed">
                  {t("pages.firstDepositBonus.submittedBody", "We will process your request within 24 working hours and credit the bonus to your MT5 account. A confirmation has been emailed to you.")}
                </p>
                <a {...bonusCta.linkProps} className="btn-solid-brand mt-6 !h-11 !px-7 inline-flex">
                  {t("pages.firstDepositBonus.makeDeposit", "Make deposit now")} <ArrowRight size={14} />
                </a>
              </div>
            )}
          </div>
        </section>

        {/* ===== 3 STEPS · brand SVGs ===== */}
        <div className="text-center mb-12 lg:mb-16">
          <div className="pill-mono inline-flex mb-4">{t("firstDepositBonus.sectionHowItWorks.pill", "Simplified process")}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1 max-w-3xl mx-auto">
            {t("firstDepositBonus.sectionHowItWorks.head1", "How it")} <span className="text-gradient-brand font-semibold">{t("firstDepositBonus.sectionHowItWorks.head2", "works")}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10 mb-24 relative">
          <div aria-hidden className="hidden md:block absolute top-10 left-[16.6%] right-[16.6%] h-px" style={{ background: "repeating-linear-gradient(90deg, color-mix(in srgb, var(--gold-2) 12%, transparent) 0 6px, transparent 6px 14px)" }} />
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="relative text-center"
            >
              <div
                className="relative w-16 h-16 mx-auto rounded-2xl grid place-items-center mb-5 z-[1]"
                style={{
                  background: "color-mix(in srgb, var(--gold-2) 6%, var(--surface))",
                  border: "1px solid color-mix(in srgb, var(--gold-2) 10%, transparent)",
                  boxShadow: "0 0 12px -10px color-mix(in srgb, var(--gold-2) 10%, transparent)",
                }}
              >
                <s.Icon size={26} strokeWidth={1.8} style={{ color: "var(--gold-2)" }} />
              </div>
              <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-gradient-gold mb-2">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-xl font-medium text-text-1 mb-3">{s.title}</h3>
              <p className="text-text-3 text-[14px] leading-relaxed max-w-xs mx-auto">{s.body}</p>
            </motion.div>
          ))}
        </div>

        {/* ===== FAQ · also anchor target for hero "Read the conditions" link ===== */}
        <div id="conditions" className="text-center mb-10 scroll-mt-28">
          <div className="pill-mono inline-flex mb-4">{t("firstDepositBonus.sectionFaq.pill", "FAQ")}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1 max-w-4xl mx-auto">
            {t("firstDepositBonus.sectionFaq.head1", "Frequently asked")} <span className="text-gradient-brand font-semibold">{t("firstDepositBonus.sectionFaq.head2", "questions")}</span>
          </h2>
        </div>
        <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden mb-12" style={{ background: "var(--surface-2)" }} data-testid="bonus-faq">
          {FAQS.map((f, i) => (
            <details key={i} className="group border-b last:border-0" style={{ borderColor: "color-mix(in srgb, var(--text-1) 5%, transparent)" }}>
              <summary className="cursor-pointer px-5 py-4 flex items-center justify-between gap-4 text-text-1 text-[14px] font-medium hover:bg-[color:var(--surface)] transition-colors">
                <span className="flex-1">{f.q}</span>
                <ArrowRight size={14} className="transition-transform group-open:rotate-90 text-text-3 shrink-0" />
              </summary>
              <div className="px-5 pb-5 text-text-2 text-[13.5px] leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>
      </div>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <div className="pill-mono inline-flex mb-5">{t("firstDepositBonus.footerCta.pill", "Ready when you are")}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1 mb-6 max-w-3xl mx-auto">
            {t("firstDepositBonus.footerCta.head1", "Open your account")} · {t("firstDepositBonus.footerCta.head2then", "then")} <span className="text-gradient-gold font-semibold">{t("firstDepositBonus.footerCta.head2", "add 50% more trading capital.")}</span>
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a {...bonusCta.linkProps} className="btn-solid-gold !h-12 !px-7" data-testid="bonus-footer-cta-primary">
              {t("firstDepositBonus.footerCta.primary", "Open Account")} <ArrowRight size={16} />
            </a>
            <a href="https://demo.ofinancial.markets/" target="_blank" rel="noopener noreferrer" className="btn-ghost-rotating btn-ghost-rotating-gold text-sm px-7 h-12 rounded-[10px]" data-testid="bonus-footer-cta-secondary">
              {t("firstDepositBonus.footerCta.secondary", "Try Demo")}
            </a>
          </div>
        </div>
      </section>

      <MarketTicker />
      <Footer />
    </div>
  );
}

function Field({ label, Icon, children }) {
  return (
    <div>
      <label className="block text-text-3 text-[12px] uppercase tracking-wider font-mono mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-3" strokeWidth={1.8} />}
        {children}
      </div>
    </div>
  );
}
